/**
 * Homepage Taster — anonymous answer store (spec 010 §3.1).
 *
 * One record per question a visitor answered on the homepage taster card. This
 * is deliberately NOT written into `satOpenProgress`: a one-question "practice
 * set" there would flip the course to `practicing` before the diagnostic
 * (009 FIRST-RUN stage rules). WS-E's first-run hero reads this store to say
 * `You already answered one {Domain} question.`
 *
 * `questionId` is the question's UUID for live-served questions and the stable
 * `source` code (e.g. `QB-AL-102`) for the bundled fixtures — fixtures never
 * hold a UUID (seeded ids are random per environment).
 *
 * Self-healing like `satOpenProgress`: malformed state is cleared and read as
 * empty; storage that throws (Safari private mode, quota) never breaks the page.
 */

export const SAT_OPEN_TASTER_KEY = 'satOpenTaster';

export interface TasterAnswer {
  questionId: string;
  selectedKey: string;
  correct: boolean;
  /** SatDomain enum name, e.g. `ALGEBRA`. */
  domain: string;
  answeredAt: string; // ISO
  /** Homepage ladder level (`EASY` | `MEDIUM` | `HARD` | `PROVE_IT`). Absent on older answers. */
  difficulty?: string;
  /** Bank `source` code when the question came from the bundled ladder. */
  sourceCode?: string;
}

export interface SatOpenTasterState {
  version: 1;
  answers: TasterAnswer[];
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

function isValidAnswer(a: unknown): a is TasterAnswer {
  return (
    isObject(a) &&
    typeof a.questionId === 'string' &&
    typeof a.selectedKey === 'string' &&
    typeof a.correct === 'boolean' &&
    typeof a.domain === 'string' &&
    typeof a.answeredAt === 'string' &&
    (a.difficulty === undefined || typeof a.difficulty === 'string') &&
    (a.sourceCode === undefined || typeof a.sourceCode === 'string')
  );
}

function isValidState(value: unknown): value is SatOpenTasterState {
  return isObject(value) && value.version === 1 && Array.isArray(value.answers) && value.answers.every(isValidAnswer);
}

/** In-memory copy of the last state whose write failed, so the session still counts it. */
let unpersisted: SatOpenTasterState | null = null;

export function clearOpenTaster(): void {
  unpersisted = null;
  try {
    localStorage.removeItem(SAT_OPEN_TASTER_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
}

/** Null when the visitor has answered nothing (or the stored state was malformed, which heals it). */
export function loadOpenTaster(): SatOpenTasterState | null {
  if (unpersisted) return unpersisted;
  let raw: string | null;
  try {
    raw = localStorage.getItem(SAT_OPEN_TASTER_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isValidState(parsed)) throw new Error('malformed taster state');
    return parsed;
  } catch {
    clearOpenTaster();
    return null;
  }
}

export function tasterAnswers(): TasterAnswer[] {
  return loadOpenTaster()?.answers ?? [];
}

export function tasterAnswerCount(): number {
  return tasterAnswers().length;
}

/** Appends one answer and returns the new state. Never throws. */
export function recordTasterAnswer(answer: TasterAnswer): SatOpenTasterState {
  const state: SatOpenTasterState = {
    version: 1,
    answers: [...tasterAnswers(), answer],
  };
  try {
    localStorage.setItem(SAT_OPEN_TASTER_KEY, JSON.stringify(state));
    unpersisted = null;
  } catch {
    unpersisted = state;
  }
  return state;
}
